import { DEFAULT_BRAND_THEME } from '../services/brandTheme'
import {
  createChartSnapshot,
  getCompatibleChartVisualOptions,
  getDefaultChartVisualOptions,
} from '../services/chartSnapshot'

function getReusableColors(colorScale) {
  const mapped = Array.isArray(colorScale?.userScaleValues)
    ? colorScale.userScaleValues.map((entry) => entry?.range)
    : []
  return [...mapped, colorScale?.defaultColor].filter(
    (color, index, values) => typeof color === 'string' && values.indexOf(color) === index
  )
}

function inheritColorScale(chart, sourceOptions) {
  const sourceColors = getReusableColors(sourceOptions?.colorScale)
  const targetScale = getDefaultChartVisualOptions(chart).colorScale
  if (!sourceColors.length || !targetScale || typeof targetScale !== 'object') return {}

  // Start with the target chart's own shape. Color-scale objects look similar
  // across charts but are not interchangeable; this avoids reintroducing the
  // incompatible scale state that can lead to RAWGraphs layout errors.
  return {
    colorScale: {
      ...targetScale,
      defaultColor: sourceColors[0],
      userScaleValues: (Array.isArray(targetScale.userScaleValues)
        ? targetScale.userScaleValues
        : []
      ).map((entry, index) => ({
        ...entry,
        range: sourceColors[index % sourceColors.length],
      })),
    },
  }
}

/** Prefer the selected chart; otherwise use the first chart on the board. */
export function getCanvasStyleSource(objects, selectedIds = []) {
  const selected = objects.find(
    (object) => selectedIds.includes(object.id) && object.kind === 'chart'
  )
  return selected || objects.find((object) => object.kind === 'chart') || null
}

/**
 * Give generated charts the active chart's presentation, while retaining
 * target-chart defaults for renderer-specific controls.
 */
export function createCanvasGenerationSnapshot({ chart, mapping, styleSource }) {
  const sourceTheme = styleSource?.brandTheme || DEFAULT_BRAND_THEME
  const snapshot = createChartSnapshot({
    chart,
    mapping,
    brandTheme: {
      ...DEFAULT_BRAND_THEME,
      ...sourceTheme,
      // Generated charts keep their own editable titles rather than inheriting
      // the reference chart's header verbatim.
      title: undefined,
    },
  })

  if (!styleSource) return snapshot

  return {
    ...snapshot,
    visualOptions: {
      ...snapshot.visualOptions,
      ...getCompatibleChartVisualOptions(chart, styleSource.visualOptions),
      ...inheritColorScale(chart, styleSource.visualOptions),
    },
  }
}
